/**
 * @description Session-scoped dismiss for the missing merchant-alias banner.
 *
 * Key features:
 * - Dismiss lasts for the current login + each company independently
 * - Company switcher can dismiss A then B without forgetting A
 * - A session id is minted on first dismiss and kept across access-token refresh
 * - /sign-in clears the keys so the next login shows the banner again
 *
 * @dependencies
 * - localStorage (survives tab close while the refresh session is still alive)
 *
 * @notes
 * - Do not key dismiss to access_token — refresh would re-show the banner mid-session
 * - Do not key dismiss to user id — that would hide the banner across logins
 * - Storage is { sessionId, companyIds[] }. Legacy `sessionId:companyId` still reads.
 * - Storage access is fail-open (show the banner). Never throw into auth or layout.
 */

export const AUTH_SESSION_ID_KEY = 'lynx_auth_session_id';
export const BANNER_DISMISS_KEY = 'missing_alias_banner_dismissed';

/**
 * @returns {string}
 */
function createSessionId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * @param {Storage} storage
 * @param {string} key
 * @returns {string|null}
 */
function safeGet(storage, key) {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * @param {Storage} storage
 * @param {string} key
 * @param {string} value
 * @returns {boolean}
 */
function safeSet(storage, key, value) {
  try {
    storage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

/**
 * @param {Storage} storage
 * @param {string} key
 */
function safeRemove(storage, key) {
  try {
    storage.removeItem(key);
  } catch {
    /* fail-open */
  }
}

/**
 * @param {string|null} raw
 * @returns {{ sessionId: string, companyIds: string[] }|null}
 */
function parseDismissState(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.sessionId === 'string' && Array.isArray(parsed.companyIds)) {
      return {
        sessionId: parsed.sessionId,
        companyIds: parsed.companyIds.filter(Boolean).map(String)
      };
    }
  } catch {
    // legacy `sessionId:companyId`
  }
  const sep = raw.indexOf(':');
  if (sep <= 0) return null;
  const companyId = raw.slice(sep + 1);
  if (!companyId) return null;
  return { sessionId: raw.slice(0, sep), companyIds: [companyId] };
}

/**
 * @param {Storage} storage
 * @returns {{ sessionId: string, companyIds: string[] }|null}
 */
function readDismissState(storage) {
  return parseDismissState(safeGet(storage, BANNER_DISMISS_KEY));
}

/**
 * @param {Storage} storage
 * @param {string} sessionId
 * @param {string[]} companyIds
 * @returns {boolean}
 */
function writeDismissState(storage, sessionId, companyIds) {
  return safeSet(
    storage,
    BANNER_DISMISS_KEY,
    JSON.stringify({ sessionId, companyIds: [...new Set(companyIds.map(String))] })
  );
}

/**
 * Mint or return the current auth-session id.
 *
 * @param {Storage} [storage]
 * @returns {string|null}
 */
export function ensureAuthSessionId(storage = localStorage) {
  let id = safeGet(storage, AUTH_SESSION_ID_KEY);
  if (!id) {
    id = createSessionId();
    if (!safeSet(storage, AUTH_SESSION_ID_KEY, id)) return null;
  }
  return id;
}

/**
 * @param {string|null|undefined} companyId
 * @param {Storage} [storage]
 * @returns {boolean}
 */
export function isBannerDismissedForCurrentSession(companyId, storage = localStorage) {
  if (!companyId) return false;
  const sessionId = safeGet(storage, AUTH_SESSION_ID_KEY);
  if (!sessionId) return false;
  const state = readDismissState(storage);
  if (!state || state.sessionId !== sessionId) return false;
  return state.companyIds.includes(String(companyId));
}

/**
 * @param {string} companyId
 * @param {Storage} [storage]
 */
export function dismissBannerForCurrentSession(companyId, storage = localStorage) {
  if (!companyId) return;
  const sessionId = ensureAuthSessionId(storage);
  if (!sessionId) return;
  const state = readDismissState(storage);
  const existing = state && state.sessionId === sessionId ? state.companyIds : [];
  writeDismissState(storage, sessionId, [...existing, String(companyId)]);
}

/**
 * Call when the user hits sign-in so the next login shows the banner.
 *
 * @param {Storage} [storage]
 */
export function clearBannerAuthSession(storage = localStorage) {
  safeRemove(storage, AUTH_SESSION_ID_KEY);
  safeRemove(storage, BANNER_DISMISS_KEY);
}
